import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAppDispatch } from "../../../store/hooks";
import {
  QuestionCreation,
  QuestionCreationError,
} from "../../../store/types/job";
import { 
  assessment, 
  pretest, 
  edit_pretest, 
  edit_assessment,
  getPretestById,
  getAssessmentById 
} from "../../../services/api/job";
import axiosInstance from "../../../utility/axiosInstance";

type QuestionType = "pretest" | "assessment";

export const usePretest = (
  type: QuestionType = "pretest",
  options?: { mode?: string; id?: string }
) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  
  // Edit mode detection - prioritize options, fallback to search params
  const editMode = options?.mode === "edit" || searchParams.get("mode") === "edit";
  const editId = options?.id || searchParams.get("id") || "";
  
  // Use the passed type parameter instead of pathname detection
  const isAssessmentPage = type === "assessment";

  const [data, dataSet] = React.useState<QuestionCreation>({
    jobTitle: "",
    questions: [""],
    duration: "",
    questionCategory: "",
  });

  const [error, errorSet] = React.useState<QuestionCreationError>({
    jobTitle: "",
    questions: "",
    duration: "",
  });

  // Fetch existing data when in edit mode
  useEffect(() => {
    const fetchData = async () => {
      if (editMode && editId) {
        try {
          setLoading(true);
          const fetchedData = isAssessmentPage 
            ? await getAssessmentById(editId)
            : await getPretestById(editId);
          
          console.log('Fetched data:', fetchedData);
          
          dataSet({
            jobTitle: fetchedData.jobTitle || "",
            description: fetchedData.description || "",
            questions: fetchedData.questions || [""],
            duration: fetchedData.duration?.toString() || "",
            questionCategory: fetchedData.questionCategory || "",
          });
          setLoading(false);
        } catch (error) {
          console.error(`Error fetching ${isAssessmentPage ? 'assessment' : 'pretest'} data:`, error);
          console.error('Full error object:', error);
          setLoading(false);
        }
      }
    };
    
    fetchData();
  }, [editMode, editId, isAssessmentPage]);

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    errorSet((prev) => ({ ...prev, [name]: "" }));
    // Filter out numbers from job title in real-time
    if (name === "jobTitle") {
      const filteredValue = value.replace(/\d/g, '');
      dataSet((prev) => ({ ...prev, [name]: filteredValue }));
    } else {
      dataSet((prev) => ({ ...prev, [name]: value }));
    }
  };

  const onChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    dataSet((prev) => {
      const newData = { ...prev, [name]: value };
      return newData;
    });
    errorSet((prev) => ({ ...prev, [name]: "" }));
  };

  const onChangeTextbox = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    dataSet((prev) => ({ ...prev, [name]: value }));
    errorSet((prev) => ({ ...prev, [name]: "" }));
  };

  const updateQuestion = (index: number, value: string) => {
    // Filter out numbers and special characters in real-time (only allow letters and spaces)
    const filteredValue = value.replace(/[^A-Za-z\s]/g, '');
    const newQuestions = [...data.questions];
    newQuestions[index] = filteredValue;
    handleInputChange("questions", newQuestions);
  };

  const handleInputChange = (name: keyof QuestionCreation, value: any) => {
    dataSet((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when field is modified
    if (error[name as keyof QuestionCreationError]) {
      errorSet((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const addQuestion = () => {
    dataSet((prev) => ({
      ...prev,
      questions: [...prev.questions, ""],
    }));
  };

  const removeQuestion = (index: number) => {
    dataSet((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }));
  };
  // const removeQuestion = (id: string) => {
  //   dataSet((prev) => ({
  //     ...prev,
  //     questions: prev.questions.filter((question) => question?.id !== id),
  //   }));
  // };

  const onValidate = (): boolean => {
    let error: boolean = true;
    
    // Job Title validation - required, min 3 characters, no numbers, no special characters
    if (!data.jobTitle || data.jobTitle.trim().length === 0) {
      errorSet((prev) => ({ ...prev, jobTitle: "Job Title is required" }));
      error = false;
    } else if (data.jobTitle.trim().length < 3) {
      errorSet((prev) => ({ ...prev, jobTitle: "Job Title must be at least 3 characters" }));
      error = false;
    } else if (/\d/.test(data.jobTitle.trim())) {
      errorSet((prev) => ({ ...prev, jobTitle: "Job Title cannot contain numbers" }));
      error = false;
    } else if (!/^[A-Za-z\s\-'().,]+$/.test(data.jobTitle.trim())) {
      errorSet((prev) => ({ ...prev, jobTitle: "Job Title can only contain letters, spaces, and basic punctuation" }));
      error = false;
    }
    
    // Questions validation - required, min 3 characters each, only letters and spaces, multiple words required, no numbers
    if (!data.questions || data.questions.length === 0) {
      errorSet((prev) => ({ ...prev, questions: "At least one question is required" }));
      error = false;
    } else {
      const invalidQuestions = data.questions.some((q, index) => {
        if (!q || q.trim().length === 0) {
          return true;
        }
        if (q.trim().length < 3) {
          return true;
        }
        // Check for numbers
        if (/\d/.test(q.trim())) {
          return true;
        }
        // Check for special characters (only allow letters and spaces)
        if (!/^[A-Za-z\s]+$/.test(q.trim())) {
          return true;
        }
        // Check for multiple words (must have at least one space)
        const words = q.trim().split(/\s+/).filter(word => word.length > 0);
        if (words.length < 2) {
          return true;
        }
        return false;
      });
      
      if (invalidQuestions) {
        const emptyQuestions = data.questions.filter(q => !q || q.trim().length === 0);
        const shortQuestions = data.questions.filter(q => q && q.trim().length > 0 && q.trim().length < 3);
        const hasNumbers = data.questions.some(q => q && /\d/.test(q.trim()));
        const hasSpecialChars = data.questions.some(q => q && !/^[A-Za-z\s]+$/.test(q.trim()));
        const singleWordQuestions = data.questions.some(q => {
          if (!q || q.trim().length === 0) return false;
          const words = q.trim().split(/\s+/).filter(word => word.length > 0);
          return words.length < 2;
        });
        
        let errorMsg = "";
        if (emptyQuestions.length > 0) {
          errorMsg = "All questions are required";
        } else if (shortQuestions.length > 0) {
          errorMsg = "Each question must be at least 3 characters";
        } else if (hasNumbers) {
          errorMsg = "Questions cannot contain numbers";
        } else if (hasSpecialChars) {
          errorMsg = "Questions can only contain letters and spaces";
        } else if (singleWordQuestions) {
          errorMsg = "Each question must contain at least two words";
        }
        errorSet((prev) => ({ ...prev, questions: errorMsg }));
        error = false;
      }
    }
    
    // Duration validation - required with error message
    if (!data.duration || data.duration.trim().length === 0) {
      errorSet((prev) => ({ ...prev, duration: "Duration is required" }));
      error = false;
    }
    
    return error;
  };

  const onSubmit = async () => {
    if (onValidate()) {
      try {
        const payload = {
          jobTitle: data.jobTitle,
          description: data.description,
          questions: data.questions,
          duration: data.duration,
        };
        setLoading(true);

        console.log("Before payload");
        console.log(payload);

        if (editMode && editId) {
          // Edit mode - call PATCH API
          const result = await edit_pretest(dispatch, editId, payload);
          if (result.success) {
            navigate(`/pretests`);
          }
        } else {
          // Create mode - call POST API
          await pretest(dispatch, payload);
          // Clear state after successful submission
          dataSet({
            jobTitle: "",
            description: "",
            questions: [],
            duration: "",
          });
          navigate(`/assessment-create`);
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    }
  };

  const onSubmit_ = async () => {
    if (onValidate()) {
      try {
        const payload = {
          jobTitle: data.jobTitle,
          description: data.description,
          questions: data.questions,
          duration: data.duration,
        };
        setLoading(true);

        console.log("Before payload");
        console.log(payload);

        if (editMode && editId) {
          // Edit mode - call PATCH API
          const result = await edit_assessment(dispatch, editId, payload);
          if (result.success) {
            navigate(`/assessments`);
          }
        } else {
          // Create mode - call POST API
          await assessment(dispatch, payload);
          // Clear state after successful submission
          dataSet({
            jobTitle: "",
            description: "",
            questions: [],
            duration: "",
          });
          navigate(`/create-job`);
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    }
  };

  return {
    data,
    error,
    loading,
    editMode,
    onChangeHandler,
    onChangeSelect,
    onChangeTextbox,
    updateQuestion,
    addQuestion,
    removeQuestion,
    onSubmit,
    onSubmit_,
  };
};
